import { array, boolean, number, object, string } from 'yup';

import { getNoun } from '@/shared/utils/localization';

// Base schema for common fields
const baseSchema = {
  // Active status
  active: boolean().required('Active status is required'),

  // Mint address (Solana token address)
  mint: string()
    .required('Token mint address is required')
    .test('is-valid-solana-address', 'Must be a valid Solana address', value => {
      if (!value) return false;
      // Solana addresses are base58 encoded strings, typically 32-44 characters
      const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
      return solanaAddressRegex.test(value);
    }),

  // Slippage
  slippage: string()
    .required('Slippage percentage is required')
    .test(
      'is-valid-percentage',
      'Must be a valid percentage greater than 0 and less than 100',
      value => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num > 0 && num <= 100;
      },
    ),
};

// Schema for create mode (wallet IDs required for both types)
export const createModeSchema = object({
  ...baseSchema,

  // Receiving wallet IDs (required on create)
  key_pairs_to: array()
    .of(number().required('Wallet ID is required'))
    .required('At least one receiving wallet must be selected')
    .min(1, 'At least one receiving wallet must be selected'),

  // Sending wallet IDs (required on create)
  key_pairs_from: array()
    .of(number().required('Wallet ID is required'))
    .required('At least one sending wallet must be selected')
    .min(1, 'At least one sending wallet must be selected'),
})
  .test(
    'to-from-ratio',
    'Each sending wallet must have exactly 4 corresponding receiving wallets (4:1 ratio)',
    function (value) {
      const { key_pairs_to, key_pairs_from } = value;

      // Both arrays should be present in create mode
      if (!key_pairs_to || !key_pairs_from) {
        return true; // Let individual field validations handle missing arrays
      }

      const receivingCount = key_pairs_to.length;
      const sendingCount = key_pairs_from.length;

      // If no sending wallets, we can't determine the required receiving wallet count
      if (sendingCount === 0) {
        return true; // Let the individual sending_wallet_ids validation handle empty array
      }

      const expectedReceivingCount = sendingCount * 4;

      if (receivingCount !== expectedReceivingCount) {
        return this.createError({
          message: `Invalid wallet ratio: you need exactly 4 receiving wallets for every 1 sending wallet. You have ${receivingCount} receiving ${getNoun(
            receivingCount,
            ['wallet', 'wallets'],
          )} and ${sendingCount} sending ${getNoun(sendingCount, [
            'wallet',
            'wallets',
          ])}, but need ${expectedReceivingCount} receiving ${getNoun(expectedReceivingCount, [
            'wallet',
            'wallets',
          ])}.`,
          path: 'key_pairs_to',
        });
      }

      return true;
    },
  )
  .test(
    'no-duplicate-wallet-ids',
    'Wallet IDs cannot appear in both receiving and sending lists',
    function (value) {
      const { key_pairs_to, key_pairs_from } = value;

      // Skip validation if either array is missing or empty
      if (
        !key_pairs_to ||
        !key_pairs_from ||
        key_pairs_to.length === 0 ||
        key_pairs_from.length === 0
      ) {
        return true;
      }

      // Create set of receiving wallet IDs for efficient lookup
      const receivingIdSet = new Set(key_pairs_to);

      // Check if any sending wallet ID exists in receiving wallets
      const duplicateIds: number[] = [];
      for (const sendingId of key_pairs_from) {
        if (receivingIdSet.has(sendingId)) {
          duplicateIds.push(sendingId);
        }
      }

      if (duplicateIds.length > 0) {
        const idList = duplicateIds.join(', ');
        return this.createError({
          message: `The following wallet ID(s) appear in both receiving and sending lists: ${idList}. Each wallet can only be used as either receiving or sending, not both.`,
          path: 'key_pairs_from',
        });
      }

      return true;
    },
  );

// Schema for edit mode (wallet IDs are optional - new wallets being added)
export const editModeSchema = object({
  ...baseSchema,

  // Receiving wallet IDs (optional in edit - new wallets to add)
  key_pairs_to: array().of(number().required('Wallet ID is required')),

  // Sending wallet IDs (optional in edit - new wallets to add)
  key_pairs_from: array().of(number().required('Wallet ID is required')),
})
  .test(
    'to-from-ratio',
    'Each sending wallet must have exactly 4 corresponding receiving wallets (4:1 ratio)',
    function (value) {
      const { key_pairs_to, key_pairs_from } = value;

      // In edit mode, only validate ratio if both arrays are provided and not empty
      const hasReceivingWallets = key_pairs_to && key_pairs_to.length > 0;
      const hasSendingWallets = key_pairs_from && key_pairs_from.length > 0;

      // Skip ratio validation if only one array is provided (partial update) or both are empty
      if (!hasReceivingWallets || !hasSendingWallets) {
        return true;
      }

      const receivingCount = key_pairs_to.length;
      const sendingCount = key_pairs_from.length;
      const expectedReceivingCount = sendingCount * 4;

      if (receivingCount !== expectedReceivingCount) {
        return this.createError({
          message: `Invalid wallet ratio: you need exactly 4 receiving wallets for every 1 sending wallet. You have ${receivingCount} receiving ${getNoun(
            receivingCount,
            ['wallet', 'wallets'],
          )} and ${sendingCount} sending ${getNoun(sendingCount, [
            'wallet',
            'wallets',
          ])}, but need ${expectedReceivingCount} receiving ${getNoun(expectedReceivingCount, [
            'wallet',
            'wallets',
          ])}.`,
          path: 'key_pairs_to',
        });
      }

      return true;
    },
  )
  .test(
    'no-duplicate-wallet-ids',
    'Wallet IDs cannot appear in both receiving and sending lists',
    function (value) {
      const { key_pairs_to, key_pairs_from } = value;

      // In edit mode, only validate if both arrays are provided and not empty
      const hasReceivingWallets = key_pairs_to && key_pairs_to.length > 0;
      const hasSendingWallets = key_pairs_from && key_pairs_from.length > 0;

      // Skip validation if only one array is provided (partial update) or both are empty
      if (!hasReceivingWallets || !hasSendingWallets) {
        return true;
      }

      // Create set of receiving wallet IDs for efficient lookup
      const receivingIdSet = new Set(key_pairs_to);

      // Check if any sending wallet ID exists in receiving wallets
      const duplicateIds: number[] = [];
      for (const sendingId of key_pairs_from) {
        if (receivingIdSet.has(sendingId)) {
          duplicateIds.push(sendingId);
        }
      }

      if (duplicateIds.length > 0) {
        const idList = duplicateIds.join(', ');
        return this.createError({
          message: `The following wallet ID(s) appear in both receiving and sending lists: ${idList}. Each wallet can only be used as either receiving or sending, not both.`,
          path: 'key_pairs_from',
        });
      }

      return true;
    },
  );
